import React from 'react'
import type { ToteSelection, AllUpSelection, Acceptor, Race } from '@mobi/betslip/types'
import { Flex } from '@mobi/component-library/Common/Flex'
import { spacing } from '@mobi/component-library/Theme/Common'
import { getAcceptorNameForSingleSelection } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/BetInfoHeader/helpers'
import { PlaceText } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/BetInfoHeader/Components/Common/PlaceText'
import { SelectionInfo } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/Common/SelectionInfo'

interface MultiRaceSelectionProps {
  selection: ToteSelection | AllUpSelection
  races: Race[]
  acceptors: Acceptor[]
}

export const MultiRaceSelection = ({ selection, races, acceptors }: MultiRaceSelectionProps) => {
  const parsed =
    selection.betType === 'All Up'
      ? parseAllUpSelections(selection as AllUpSelection, races, acceptors)
      : parseMultiLegSelectionString(selection as ToteSelection, races, acceptors)
  return (
    <Flex flexDirection='column' gap={spacing.smx1} testId='MultiRaceSelection'>
      {Object.keys(parsed).map(key => (
        <SelectionInfo key={`__${key}`} leftSlot={<PlaceText text={key} />} text={parsed[key]} />
      ))}
    </Flex>
  )
}

// =============
// Local Helpers
// =============

function parseAllUpSelections(
  { details }: AllUpSelection,
  races: Race[],
  allAcceptors: Acceptor[]
): MultiRaceSelections {
  if (details.length !== races.length) {
    // don't return anything because it's probably wrong
    return {}
  }

  const parsed: MultiRaceSelections = {}
  details.forEach((selectionDetails, index) => {
    const race = races[index]
    const key = `Race ${race.raceNumber}`
    parsed[key] = `(${getPoolTypeName(
      selectionDetails.poolType
    )}) ${getAcceptorNameForSingleSelection(
      selectionDetails.betSelections,
      getAcceptorsForRace(race, allAcceptors)
    )}`
  })

  return parsed
}

function parseMultiLegSelectionString(
  { selectionString }: ToteSelection,
  races: Race[],
  allAcceptors: Acceptor[]
): MultiRaceSelections {
  const selectionStringParts = selectionString.split('/')

  if (selectionStringParts.length !== races.length) {
    // don't return anything because it's probably wrong
    return {}
  }

  const parsed: MultiRaceSelections = {}
  selectionStringParts.forEach((selection, index) => {
    const race = races[index]
    const key = `Race ${race.raceNumber}`
    parsed[key] = getAcceptorNameForSingleSelection(
      selection,
      getAcceptorsForRace(race, allAcceptors)
    )
  })

  return parsed
}

function getPoolTypeName(poolType: AllUpSelection['details'][0]['poolType']): string {
  switch (poolType) {
    case 'W': {
      return 'Win'
    }
    case 'P': {
      return 'Place'
    }
    case 'EW': {
      return 'Eachway'
    }
    case 'Q': {
      return 'Quinella'
    }
    default: {
      return ''
    }
  }
}
function getAcceptorsForRace(race: Race, allAcceptors: Acceptor[]): Acceptor[] {
  return allAcceptors.filter(acceptor => race.acceptorKeys.includes(acceptor.key))
}

// =====
// Types
// =====

type MultiRaceSelections = Record<string, string>
